import React, { useRef } from 'react';
import Layout from 'components/layout';
import Hero from './hero';
import Quote, { QuoteAuthor } from '../quote/quote';
import CardStack from './card-stack';
import BuiltForThis from './built-for-this';
import PowerOfExp from './power-of-exp';
import VideoCardCarousel from './video-card-carousel';
import JoinExpFaq from './faq';
import { JoinExpConfig } from 'tenants';
import Programs from './programs';
import FloatingActionButton from 'components/floating-action-button';
import { useIsMobile } from 'hooks/use-size-class';

import { useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';

type JoinExpEventHandlerProps = {
  onLearnMoreClick: () => void;
  onBuiltForThisJoinUsClick: () => void;
  onSignMeUpClick: () => void;
  onGetNowClick: () => void;
  onProgramButtonClick: (index: number) => void;
  onFabClick?: () => void;
};

type JoinExpProps = {
  config: Omit<JoinExpConfig, 'learnMoreClickEvent' | 'builtForThisJoinUsClickEvent' | 'signMeUpClickEvent' | 'getNowClickEvent'>;
} & JoinExpEventHandlerProps;

function JoinExp({ config, onSignMeUpClick, onGetNowClick, onProgramButtonClick, onFabClick }: JoinExpProps) {
  const { t, i18n } = useTranslation([Namespaces.JOIN_EXP_CA, Namespaces.JOIN_EXP_US, Namespaces.COMMON]);
  const downArrowRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const { quotes, videoCardData, cardStackData, programsData, faq, powerOfExpData, builtForThis, fabDestination } = config;

  const glennQuote = quotes?.glenn;
  const leoQuote = quotes?.leo;

  const glennQuoteAuthor: QuoteAuthor = {
    name: glennQuote?.authorName || '',
    title: glennQuote?.authorTitle || '',
    image: glennQuote?.authorImage || '',
    imageAlt: glennQuote?.authorImageAlt || '',
  };

  const leoQuoteAuthor: QuoteAuthor = {
    name: leoQuote?.authorName || '',
    title: leoQuote?.authorTitle || '',
    image: leoQuote?.authorImage || '',
    imageAlt: leoQuote?.authorImageAlt || '',
  };

  return (
    <Layout joinExpFabDestination={fabDestination}>
      <Hero downArrowRef={downArrowRef} />
      <div ref={downArrowRef}/>
      <>
        <VideoCardCarousel videoCards={videoCardData} />
        {glennQuote && (
          <Quote
            author={glennQuoteAuthor}
            content={glennQuote.quoteContent}
            style={{ highlightColor: glennQuote.highlightColor }}
          />
        )}
        <CardStack stack={cardStackData} />
        <Programs
          programs={programsData}
          onButtonClick={onProgramButtonClick}
        />
        {leoQuote && (
          <Quote 
            author={leoQuoteAuthor} 
            content={leoQuote.quoteContent} 
            style={{ highlightColor: leoQuote.highlightColor }}
          />
        )}
        <BuiltForThis
          logo={builtForThis.logo[i18n.language || 'en']}
          video={builtForThis.video[i18n.language || 'en']}
        />
        <PowerOfExp
          powerOfExpData={powerOfExpData}
          onSignMeUpClick={onSignMeUpClick}
          onGetNowClick={onGetNowClick}
        />
        <JoinExpFaq
          title={faq.title}
          items={faq.content}
          disableHoverExpansion
        />
      </>
      <FloatingActionButton 
        label={t(LOCALE_COMMON_PATHS.BUTTON_JOINNOW, { defaultValue: 'Join Now' })}
        destination={fabDestination}
        onClick={onFabClick}
        showAfterScroll={isMobile}
      />
    </Layout>
  );
}

export default JoinExp;
